"use client"

import Link from "next/link"
import { useMemo } from "react"
import { ChevronDown } from "lucide-react"
import { cn } from "@/lib/utils"
import {
  NAV_LINKS,
  NAV_GROUPS,
  NavGroup,
  NavLink,
  NavSection,
  filterNavLinks,
  isActivePath,
  isGroupChildActive,
  SECTIONS,
} from "./nav-config"

type Variant = "desktop" | "mobile"

type OpenState = Record<string, boolean>

function SectionHeader({
  section,
  open,
  onToggle,
}: {
  section: NavSection
  open: boolean
  onToggle: () => void
}) {
  return (
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between px-3 py-2 mb-1 text-[11px] font-semibold text-gray-400 dark:text-zinc-500 uppercase tracking-wider hover:text-gray-600 dark:hover:text-zinc-300 transition-colors"
    >
      <span>{section}</span>
      <ChevronDown className={cn("w-3 h-3 transition-transform duration-200", open ? "rotate-180" : "")} />
    </button>
  )
}

function NavLinkItem({
  link,
  active,
  collapsed,
}: {
  link: NavLink
  active: boolean
  collapsed: boolean
}) {
  const Icon = link.icon

  return (
    <div className="relative group/item">
      <Link
        href={link.href}
        className={cn(
          "relative flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors",
          collapsed && "justify-center",
          active
            ? "bg-blue-600 dark:bg-blue-600 text-white shadow-md shadow-blue-500/20"
            : "text-gray-500 dark:text-zinc-400 hover:bg-gray-100 dark:hover:bg-zinc-800 hover:text-gray-900 dark:hover:text-zinc-100"
        )}
      >
        {/* subtle brand accent indicator */}
        <span
          aria-hidden
          className={cn(
            "absolute left-1 top-1.5 bottom-1.5 w-[3px] rounded-full transition-colors",
            active ? "bg-transparent" : "bg-transparent group-hover/item:bg-blue-200/80 dark:group-hover/item:bg-blue-900/40"
          )}
        />

        <Icon
          className={cn(
            collapsed ? "w-6 h-6" : "w-5 h-5",
            "flex-shrink-0 transition-transform duration-300",
            !active && "group-hover/item:scale-110"
          )}
        />

        {!collapsed && <span className="text-sm font-medium truncate">{link.label}</span>}

        {!collapsed && link.badge && (
          <span
            className={cn(
              "ml-auto text-[10px] px-2 py-0.5 rounded-full font-bold tracking-wider",
              active
                ? "bg-white/20 text-white"
                : "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"
            )}
          >
            {link.badge}
          </span>
        )}
      </Link>

      {collapsed && (
        <div className="absolute left-full top-1/2 -translate-y-1/2 ml-3 px-3 py-1.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-xs rounded-md opacity-0 group-hover/item:opacity-100 pointer-events-none transition-opacity duration-200 whitespace-nowrap z-50 shadow-xl font-medium">
          {link.label}
          <div className="absolute left-0 top-1/2 -translate-y-1/2 -ml-1 w-2 h-2 bg-gray-900 dark:bg-white rotate-45" />
        </div>
      )}
    </div>
  )
}

function NavGroupItem({
  group,
  pathname,
  collapsed,
  openGroups,
  setOpenGroups,
}: {
  group: NavGroup
  pathname: string
  collapsed: boolean
  openGroups: OpenState
  setOpenGroups: (next: OpenState) => void
}) {
  const Icon = group.icon
  const isAnyChildActive = group.children.some((c) => isGroupChildActive(c.href, pathname))
  const isOpen = openGroups[group.id] !== undefined
    ? openGroups[group.id]
    : isAnyChildActive

  const toggleGroup = () => {
    setOpenGroups({ ...openGroups, [group.id]: !isOpen })
  }

  if (collapsed) {
    return (
      <div className="relative group/item">
        <button
          onClick={toggleGroup}
          className={cn(
            "relative flex items-center justify-center w-full px-3 py-2.5 rounded-xl transition-all duration-200",
            isAnyChildActive
              ? "bg-blue-600 text-white shadow-md shadow-blue-500/20"
              : "text-gray-500 dark:text-zinc-400 hover:bg-gray-100 dark:hover:bg-zinc-800 hover:text-gray-900 dark:hover:text-zinc-100"
          )}
        >
          <span
            aria-hidden
            className={cn(
              "absolute left-1 top-1.5 bottom-1.5 w-[3px] rounded-full transition-colors",
              isAnyChildActive ? "bg-transparent" : "bg-transparent group-hover/item:bg-blue-200/80 dark:group-hover/item:bg-blue-900/40"
            )}
          />
          <Icon className="w-6 h-6 flex-shrink-0" />
        </button>
        <div className="absolute left-full top-1/2 -translate-y-1/2 ml-3 px-3 py-1.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-xs rounded-md opacity-0 group-hover/item:opacity-100 pointer-events-none transition-opacity duration-200 whitespace-nowrap z-50 shadow-xl font-medium">
          {group.label}
          <div className="absolute left-0 top-1/2 -translate-y-1/2 -ml-1 w-2 h-2 bg-gray-900 dark:bg-white rotate-45" />
        </div>

        {/* Quick popover of group links when open */}
        {isOpen && (
          <div className="absolute left-full top-1/2 -translate-y-1/2 ml-3 w-56 bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl border border-zinc-200/70 dark:border-zinc-800 p-2 z-50">
            <div className="text-xs font-bold text-gray-400 dark:text-zinc-500 uppercase tracking-wider px-2 py-1">
              {group.label}
            </div>
            <div className="space-y-1">
              {group.children.map((child) => {
                const active = isGroupChildActive(child.href, pathname)
                return (
                  <Link
                    key={child.href}
                    href={child.href}
                    className={cn(
                      "flex items-center gap-2 px-3 py-2 rounded-xl text-sm transition-colors",
                      active
                        ? "bg-blue-600 text-white"
                        : "text-gray-600 dark:text-zinc-300 hover:bg-gray-100 dark:hover:bg-zinc-800"
                    )}
                  >
                    <span className={cn("w-1.5 h-1.5 rounded-full", active ? "bg-white" : "bg-gray-300 dark:bg-zinc-600")} />
                    {child.label}
                  </Link>
                )
              })}
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div>
      <button
        onClick={toggleGroup}
        className={cn(
          "relative flex items-center gap-3 w-full px-3 py-2.5 rounded-xl transition-all duration-200",
          isAnyChildActive
            ? "bg-blue-50 dark:bg-blue-950/30 text-blue-600 dark:text-blue-400"
            : "text-gray-500 dark:text-zinc-400 hover:bg-gray-100 dark:hover:bg-zinc-800 hover:text-gray-900 dark:hover:text-zinc-100"
        )}
      >
        <span
          aria-hidden
          className={cn(
            "absolute left-1 top-1.5 bottom-1.5 w-[3px] rounded-full transition-colors",
            isAnyChildActive ? "bg-transparent" : "bg-transparent group-hover:bg-blue-200/80 dark:group-hover:bg-blue-900/40"
          )}
        />
        <Icon className="w-5 h-5 flex-shrink-0" />
        <span className="text-sm font-medium flex-1 text-left truncate">{group.label}</span>
        <ChevronDown className={cn("w-4 h-4 flex-shrink-0 transition-transform duration-200", isOpen ? "rotate-180" : "")} />
      </button>

      <div className={cn("overflow-hidden transition-[max-height] duration-300 ease-in-out", isOpen ? "max-h-96" : "max-h-0")}>
        <div className="ml-4 mt-1 mb-1 pl-4 border-l-2 border-gray-100 dark:border-zinc-800 space-y-0.5">
          {group.children.map((child) => {
            const active = isGroupChildActive(child.href, pathname)
            return (
              <Link
                key={child.href}
                href={child.href}
                className={cn(
                  "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200",
                  active
                    ? "bg-blue-600 text-white shadow-sm shadow-blue-500/20"
                    : "text-gray-500 dark:text-zinc-400 hover:bg-gray-100 dark:hover:bg-zinc-800 hover:text-gray-900 dark:hover:text-zinc-100"
                )}
              >
                <span className={cn("w-1.5 h-1.5 rounded-full flex-shrink-0", active ? "bg-white" : "bg-gray-300 dark:bg-zinc-600")} />
                {child.label}
              </Link>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export function NavList({
  pathname,
  isAdmin,
  isDeveloper,
  collapsed,
  openSections,
  setOpenSections,
  openGroups,
  setOpenGroups,
  variant,
  onNavigate,
}: {
  pathname: string
  isAdmin: boolean
  isDeveloper: boolean
  collapsed: boolean
  openSections: OpenState
  setOpenSections: (next: OpenState) => void
  openGroups: OpenState
  setOpenGroups: (next: OpenState) => void
  variant: Variant
  onNavigate?: () => void
}) {
  const links = useMemo(() => filterNavLinks(NAV_LINKS, { isAdmin, isDeveloper }), [isAdmin, isDeveloper])

  const sectionGroups = useMemo(() => {
    return Object.fromEntries(
      SECTIONS.map((section) => {
        const sectLinks = links.filter((l) => l.section === section)
        const sectGroups = NAV_GROUPS.filter((g) => g.section === section)
        return [section, { links: sectLinks, groups: sectGroups }]
      })
    ) as Record<NavSection, { links: NavLink[]; groups: NavGroup[] }>
  }, [links])

  return (
    <div className={cn(variant === "desktop" ? "space-y-2" : "space-y-6")}>
      {SECTIONS.map((section) => {
        const { links: sectLinks, groups: sectGroups } = sectionGroups[section]
        if (sectLinks.length === 0 && sectGroups.length === 0) return null

        if (collapsed) {
          return (
            <div key={section} className="pb-2 mb-2 border-b border-gray-100 dark:border-zinc-800/50 last:border-0">
              {sectLinks.map((link) => (
                <div key={link.href} onClick={onNavigate}>
                  <NavLinkItem link={link} active={isActivePath(pathname, link.href)} collapsed={true} />
                </div>
              ))}
              {sectGroups.map((group) => (
                <div key={group.id} onClick={onNavigate}>
                  <NavGroupItem
                    group={group}
                    pathname={pathname}
                    collapsed={true}
                    openGroups={openGroups}
                    setOpenGroups={setOpenGroups}
                  />
                </div>
              ))}
            </div>
          )
        }

        const isOpen = openSections[section] ?? true

        return (
          <div key={section}>
            <SectionHeader
              section={section}
              open={isOpen}
              onToggle={() => setOpenSections({ ...openSections, [section]: !isOpen })}
            />

            <div
              className={cn(
                "space-y-1 overflow-hidden transition-[max-height] duration-300 ease-in-out",
                isOpen ? "max-h-[800px] opacity-100" : "max-h-0 opacity-0"
              )}
            >
              {sectLinks.map((link) => (
                <div key={link.href} onClick={onNavigate}>
                  <NavLinkItem link={link} active={isActivePath(pathname, link.href)} collapsed={false} />
                </div>
              ))}
              {sectGroups.map((group) => (
                <div key={group.id}>
                  <NavGroupItem
                    group={group}
                    pathname={pathname}
                    collapsed={false}
                    openGroups={openGroups}
                    setOpenGroups={setOpenGroups}
                  />
                </div>
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}
